import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Hourglass, Inbox, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Label } from "./ui/label";
import { Skeleton } from "./ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { Textarea } from "./ui/textarea";
import { api, errMsg, fmtDate, fmtINR } from "../lib/api";
import { StaffLabel, useStaffLabel } from "./StaffAvatar";

export const PROPOSAL_STATUS_META = {
  pending: { label: "Pending approval", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-rose-100 text-rose-800 border-rose-200" },
  withdrawn: { label: "Withdrawn", cls: "bg-muted text-muted-foreground" },
  superseded: { label: "Superseded by Admin", cls: "bg-muted text-muted-foreground" },
};

function DecisionDialog({ code, proposal, action, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const approve = action === "approve";
  const collectorLabel = useStaffLabel(code, { userId: proposal.collector_id, name: proposal.collector_name });
  const proposerLabel = useStaffLabel(code, { userId: proposal.proposed_by, name: proposal.proposed_by_name });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/t/${code}/targets/proposals/${proposal.id}/${action}`, { note: note.trim() || null });
      toast.success(approve
        ? `Approved — ${collectorLabel}'s target for ${proposal.month} is now ${fmtINR(proposal.proposed_amount)}`
        : `Rejected ${proposerLabel}'s proposal for ${collectorLabel}`);
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="proposal-decision-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base flex items-center gap-2">
            {approve ? <CheckCircle2 className="h-4 w-4 text-emerald-700" /> : <XCircle className="h-4 w-4 text-rose-700" />}
            {approve ? "Approve" : "Reject"} target proposal
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
            <p className="font-medium"><StaffLabel code={code} userId={proposal.collector_id} name={proposal.collector_name} /> <span className="text-muted-foreground font-normal">· {proposal.month}</span></p>
            <p className="text-xs text-muted-foreground">
              {fmtINR(proposal.current_amount)} → <b className="text-foreground tabular-nums">{fmtINR(proposal.proposed_amount)}</b> · proposed by <StaffLabel code={code} userId={proposal.proposed_by} name={proposal.proposed_by_name} />
              {proposal.note && <> — “{proposal.note}”</>}
            </p>
          </div>
          <div className="space-y-1">
            <Label>Note to the supervisor <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="proposal-decision-note" />
          </div>
          <Button type="submit" className="w-full" variant={approve ? "default" : "destructive"} disabled={busy} data-testid="proposal-decision-submit">
            {busy ? "Saving..." : approve ? `Approve ${fmtINR(proposal.proposed_amount)}` : "Reject proposal"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Admin list of supervisor target proposals with approve / reject.
 * `pendingOnly` hides the status tabs (used inside the Collector targets tab); `month` filters — or `months` (array of
 * 'YYYY-MM', Phase 175: every month touched by a date range) with `periodLabel` for the heading.
 * `onChanged` fires after any decision so parents can refresh targets.
 */
export function TargetProposalsPanel({ code, month, months, periodLabel, pendingOnly = false, onChanged, compact = false, readOnly = false }) {
  const monthsKey = (months || []).join(",");
  const period = periodLabel || month || "";
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [decision, setDecision] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const params = { status: pendingOnly ? "pending" : status };
      if (monthsKey) params.months = monthsKey;
      else if (month) params.month = month;
      const r = await api.get(`/t/${code}/targets/proposals`, { params });
      setData(r.data);
    } catch (e) { setError(errMsg(e)); }
  }, [code, month, monthsKey, status, pendingOnly]);
  useEffect(() => { load(); }, [load]);

  const done = () => { setDecision(null); load(); onChanged && onChanged(); };
  const counts = data?.counts || {};
  const items = data?.proposals || [];

  if (error) {
    return <p className="text-xs text-muted-foreground" data-testid="target-proposals-error">Target proposals unavailable: {error}</p>;
  }
  if (pendingOnly && data && items.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="target-proposals-panel">
      {!pendingOnly && (
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList className="h-8">
            {["pending", "approved", "rejected", "all"].map((s) => (
              <TabsTrigger key={s} value={s} className="text-xs capitalize" data-testid={`target-proposals-filter-${s}`}>
                {s}{s !== "all" && counts[s] != null ? ` (${counts[s]})` : ""}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}
      {pendingOnly && (
        <p className="text-sm font-medium flex items-center gap-2">
          <Hourglass className="h-4 w-4 text-amber-700" /> {items.length} target proposal{items.length === 1 ? "" : "s"} awaiting your approval{period ? ` for ${period}` : ""}
        </p>
      )}
      {!data ? (
        <div className="space-y-2"><Skeleton className="h-16" /><Skeleton className="h-16" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed px-3 py-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-2" data-testid="target-proposals-empty">
          <Inbox className="h-5 w-5" />
          {status === "pending" ? "No target proposals are waiting for approval." : `No ${status === "all" ? "" : status + " "}proposals${period ? ` for ${period}` : ""}.`}
          <span className="text-xs">Supervisors with “View & edit” on the Team Scorecard module can propose collector targets.</span>
        </div>
      ) : (
        <div className="space-y-2" data-testid="target-proposals-list">
          {items.map((p) => {
            const meta = PROPOSAL_STATUS_META[p.status] || PROPOSAL_STATUS_META.pending;
            return (
              <Card key={p.id} className="shadow-sm" data-testid={`target-proposal-${p.id}`}>
                <CardContent className={`${compact ? "p-3" : "p-4"} flex items-center justify-between gap-3 flex-wrap`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                      <StaffLabel code={code} userId={p.collector_id} name={p.collector_name} />
                      {p.is_sub && <Badge variant="outline" className="text-[10px]">Sub-user of <StaffLabel code={code} userId={p.parent_user_id} name={p.parent_user_name} className="ml-1" /></Badge>}
                      <span className="text-muted-foreground font-normal">· {p.month}</span>
                      <Badge variant="outline" className={`text-[10px] ${meta.cls}`}>{meta.label}</Badge>
                    </div>
                    <p className="text-sm tabular-nums">
                      <span className="text-muted-foreground">{fmtINR(p.current_amount)}</span>
                      <span className="text-muted-foreground mx-1.5">→</span>
                      <b>{fmtINR(p.proposed_amount)}</b>
                      {p.status !== "pending" && p.live_target != null && p.live_target !== p.proposed_amount && (
                        <span className="text-xs text-muted-foreground"> · current {fmtINR(p.live_target)}</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Proposed by <StaffLabel code={code} userId={p.proposed_by} name={p.proposed_by_name} />{p.supervisor_name && p.supervisor_id !== p.proposed_by ? <> (team of <StaffLabel code={code} userId={p.supervisor_id} name={p.supervisor_name} />)</> : ""} · {fmtDate(p.created_at)}
                      {p.note && <> · “{p.note}”</>}
                      {p.decided_by_name && <> · {p.status} by <StaffLabel code={code} userId={p.decided_by} name={p.decided_by_name} />{p.decision_note ? ` — “${p.decision_note}”` : ""}</>}
                    </p>
                  </div>
                  {p.status === "pending" && !readOnly && (
                    <div className="flex items-center gap-2 shrink-0">
                      <Button variant="outline" size="sm" className="h-8" onClick={() => setDecision({ proposal: p, action: "reject" })}
                        data-testid={`target-proposal-reject-${p.id}`}>
                        <XCircle className="h-3.5 w-3.5 mr-1.5" /> Reject
                      </Button>
                      <Button size="sm" className="h-8" onClick={() => setDecision({ proposal: p, action: "approve" })}
                        data-testid={`target-proposal-approve-${p.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Approve
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      {decision && (
        <DecisionDialog code={code} proposal={decision.proposal} action={decision.action}
          onClose={() => setDecision(null)} onDone={done} />
      )}
    </div>
  );
}
